/**
 * Gibbs Text-to-Action — Public API
 *
 * Main entry point for converting natural language to validated action plans.
 *
 * @module gibbs/text-to-action
 */

import { routeRequest, isWorkflowRequest } from './routeRequest';
import { serializeWorkspace } from './serializeWorkspace';
import { planActions } from './planActions';
import { validatePlan } from './validatePlan';
import { resumeFromClarification } from './resumeFromClarification';
import { createFromActionPlan } from '../workflow/builders';
import { getAction } from '../registry/index';
import {
    matchDesignGoalForDisease,
    buildDiseaseTargetClarification,
    tryDockAndScoreShortcut,
    tryHitDiscoveryShortcut,
    tryVirtualScreeningShortcut,
    tryDeNovoShortcut,
    matchCampaignIntent,
    tryCampaignShortcut,
    tryWorkflowTemplateShortcut,
    detectTemplateAlias,
} from '../knowledge';
import { resolve as resolveAnchor } from './anchorResolver';
import {
    buildTargetSelectionClarification,
    buildTargetConfirmationClarification,
    buildGenericTargetClarification,
} from './anchorClarifications';
import { getRecentEntries } from '../conversationHistory';
import { logLLMCall } from '../telemetry';

/**
 * Validate an LLM-produced clarify intent before surfacing it to the user.
 *
 * A clarify response must include `context.actionId` so that when the user
 * answers, `resumeFromClarification` knows which action to resume. Without
 * it, the user gets the dreaded "I could not find the action to resume"
 * error on their answer.
 *
 * This validator catches malformed clarify intents at classification time
 * so the retry loop can re-prompt the LLM with a corrective message,
 * instead of letting the broken clarify reach the user and wasting a
 * round-trip.
 *
 * @param {Object} classified - The classified intent from the LLM
 * @returns {{valid: boolean, errors: string[]}}
 */
function validateClarifyIntent(classified) {
    const errors = [];

    if (!classified || classified.intent !== 'clarify') {
        return { valid: true, errors };
    }

    if (!classified.question || typeof classified.question !== 'string') {
        errors.push('clarify intent is missing a "question" field');
    }

    const ctx = classified.context;
    if (!ctx || typeof ctx !== 'object') {
        errors.push(
            'clarify intent is missing a "context" object. You must'
            + ' include context.actionId pointing to the registered action'
            + ' whose parameters you are asking about. Without it, the'
            + " user's answer cannot be resolved."
        );
        return { valid: false, errors };
    }

    // Cascade clarifications carry their own context shape and don't
    // need a single context.actionId (campaign clarifications carry
    // options[].value instead; disease_target_selection has its own
    // downstream routing). Skip the actionId check for these cascades.
    if (ctx.cascade === 'disease_target_selection'
        || ctx.cascade === 'campaign_verb'
        || ctx.cascade === 'target_selection'
        || ctx.cascade === 'target_confirmation'
        || ctx.cascade === 'generic_target') {
        return { valid: true, errors };
    }

    if (!ctx.actionId || typeof ctx.actionId !== 'string') {
        errors.push(
            'clarify intent context.actionId is missing or not a string.'
            + ' Set it to the registered actionId whose parameters you'
            + ' are asking about.'
        );
        return { valid: false, errors };
    }

    const actionDef = getAction(ctx.actionId);
    if (!actionDef) {
        errors.push(
            `clarify intent context.actionId "${ctx.actionId}" is not a`
            + ' registered action. Only use actionIds that appear in the'
            + ' "Available Actions" list in the system prompt.'
        );
        return { valid: false, errors };
    }

    const allParams = [
        ...(actionDef.inputs || []),
        ...(actionDef.parameters || []),
    ];

    // If the LLM said a specific param is missing, verify it's a real
    // param on the declared action.
    if (Array.isArray(classified.missing) && classified.missing.length > 0) {
        const paramNames = new Set(allParams.map((p) => p.name));
        const invalidMissing = classified.missing.filter(
            (name) => !paramNames.has(name)
        );
        if (invalidMissing.length > 0) {
            errors.push(
                `clarify intent references missing parameters [${invalidMissing.join(', ')}]`
                + ` that do not exist on action "${ctx.actionId}". Valid`
                + ` params for this action: [${[...paramNames].join(', ')}]`
            );
        }
    }

    // Validate clarification option VALUES against the param's enum.
    // The LLM sometimes invents option values that don't exist in the
    // registry (e.g., "full flexibility" when the param only has
    // ['Rigid']). When the user clicks a hallucinated option, the
    // downstream action handler either ignores it or crashes.
    if (Array.isArray(classified.options) && classified.options.length > 0
        && Array.isArray(classified.missing) && classified.missing[0]) {
        const paramName = classified.missing[0];
        const paramDef = allParams.find((p) => p.name === paramName);
        if (paramDef && Array.isArray(paramDef.options)
            && paramDef.options.length > 0) {
            const validValues = new Set(paramDef.options);
            const optionValues = classified.options
                .map((o) => o.value)
                .filter((v) => typeof v === 'string');
            const invalidValues = optionValues.filter(
                (v) => !validValues.has(v)
            );
            if (invalidValues.length > 0) {
                errors.push(
                    `clarify options for param "${paramName}" include`
                    + ` invalid values [${invalidValues.join(', ')}].`
                    + ` Valid values for ${paramName} on action`
                    + ` "${ctx.actionId}" are:`
                    + ` [${paramDef.options.join(', ')}].`
                    + ' Use ONLY these values as option values.'
                );
            }
        }
    }

    return { valid: errors.length === 0, errors };
}

/**
 * Destructive-confirm interceptor (gibbs-destructive-ops-ux, 2026-05-03).
 *
 * Inspect a plan-class result for any action whose ActionDef has
 * `destructiveConfirm: true`. If found, replace the plan with a
 * clarify intent that stages the original plan + the user's message,
 * and lets `resumeFromClarification` re-dispatch on confirm.
 *
 * No-op for plans without destructive actions, and for the synthetic
 * dispatch path used by the destructive-confirm cascade itself (the
 * `bypassDestructiveConfirm` flag on workspaceState breaks the
 * recursion so re-dispatching a staged plan doesn't trip the
 * interceptor again).
 *
 * @param {Object} planResult - The `{ type: 'plan', plan, ... }` result
 *   that the chain is about to return.
 * @param {string} userMessage - The original user message that produced
 *   this plan.
 * @param {Object} workspaceState - Workspace state (may carry the
 *   `bypassDestructiveConfirm` flag during cascade resume).
 * @returns {Object} The original planResult, OR a clarification
 *   intent with `cascade: 'destructive_confirm'`.
 * @private
 */
function maybeInterceptDestructiveConfirm(planResult, userMessage, workspaceState) {
    if (!planResult || planResult.type !== 'plan') return planResult;
    if (workspaceState?.bypassDestructiveConfirm) return planResult;

    const actions = planResult.plan?.actions || [];
    const destructiveActions = [];
    for (const a of actions) {
        const def = getAction(a.actionId);
        if (def?.destructiveConfirm === true) {
            destructiveActions.push({ action: a, def });
        }
    }

    if (destructiveActions.length === 0) return planResult;

    // Use the first destructive action's confirmMessage as the question.
    // If the plan contains multiple destructive actions (rare), append a
    // note so the user knows the consequences are cumulative.
    const first = destructiveActions[0];
    const extraNote = destructiveActions.length > 1
        ? `\n\n(Plan contains ${destructiveActions.length} destructive `
            + 'steps; confirming applies to all of them.)'
        : '';

    return {
        type: 'clarification',
        clarification: {
            intent: 'clarify',
            question: first.def.confirmMessage + extraNote,
            options: [
                {
                    label: 'Confirm — proceed',
                    value: { decision: 'confirm' },
                },
                {
                    label: 'Cancel',
                    value: { decision: 'cancel' },
                },
            ],
            missing: [],
            context: {
                cascade: 'destructive_confirm',
                actionId: first.action.actionId,
                stagedPlan: planResult.plan,
                stagedTier: planResult.tier,
                originalMessage: userMessage,
            },
        },
        tier: planResult.tier,
        parseErrors: planResult.parseErrors || [],
    };
}

/**
 * Convert a natural language message to a validated action plan.
 *
 * This is the main entry point for the text-to-action pipeline.
 *
 * Routing chain (do not reorder without coordinating with cascade-routing tests):
 *   0a.   Disease cascade (matchDesignGoalForDisease)
 *   0a2.  Workflow-template loader (matchWorkflowTemplateIntent) — fires
 *         when the message names one of the 7 templates AND uses a
 *         template/workflow/pipeline trigger word. Sits ahead of the
 *         campaign shortcut so "load the hit-to-lead template" routes
 *         here while bare "hit-to-lead" still routes to campaign.
 *   0aa.  Campaign-verb shortcut
 *   0b.   Dock-and-score shortcut
 *   0c.   Hit-discovery shortcut
 *   0d.   Virtual-screening shortcut
 *   0e.   De-novo shortcut
 *   0f.   Anchor resolver (NEW — gates LLM workflow tier)
 *   1+.   LLM tier with retry loop
 *
 * Steps inside `textToAction`:
 * 1. Route to appropriate LLM tier (command vs workflow)
 * 2. Parse LLM response into classified intent
 * 3. Compose action plan from classified intent
 * 4. Validate the plan against the registry
 *
 * @param {string} userMessage - Natural language input
 * @param {Object} workspaceState - BMaps workspace state
 * @returns {Promise<TextToActionResult>}
 */
export async function textToAction(userMessage, workspaceState, opts = {}) {
    // Step 0: Disease-goal short-circuit. If the user's prompt looks like
    // a high-level "design a drug for X" goal AND we recognize X as a
    // disease we have seeded in the knowledge table, skip the LLM call
    // entirely and return a clarification asking which drug target they
    // want to pursue. This is the L0 → L1 stage of the abstraction-ladder
    // cascade described in docs/gibbs_abstract_goal_decomposition_spec.md.
    const diseaseMatch = matchDesignGoalForDisease(userMessage);
    if (diseaseMatch) {
        // 2026-06-02: detect a workflow-template alias in the user's
        // message (without requiring a trigger word) so the cascade can
        // emit a multi-step plan with loadPdbId pre-filled instead of
        // just the acquire step. See diseaseCascade.
        // buildTemplateWorkflowWithPreFilledPdb.
        const workflowIntent = detectTemplateAlias(userMessage);
        const clarification = buildDiseaseTargetClarification(
            diseaseMatch.slug, workflowIntent,
        );
        if (clarification.intent === 'clarify') {
            return {
                type: 'clarification',
                clarification,
                tier: 'disease_cascade',
                parseErrors: [],
            };
        }
        if (clarification.intent === 'chat') {
            return {
                type: 'chat',
                message: clarification.message || '',
                tier: 'disease_cascade',
                chatSource: 'shortcut',
                parseErrors: [],
            };
        }
    }

    // Step 0a2: Workflow-template loader. Phrases like "load the
    // hit-to-lead template", "open the selectivity analysis workflow",
    // "use the building block search pipeline" route to one of the 7
    // templates declared in gibbs/workflow/templates.js. Fires only
    // when a trigger word (template/workflow/pipeline/recipe) is paired
    // with a template alias — keeps it disjoint from the campaign tier
    // (bare "hit-to-lead" still routes to campaign as a verb).
    const templateResult = tryWorkflowTemplateShortcut(userMessage);
    if (templateResult && templateResult.intent === 'workflow') {
        // Template workflows intentionally contain empty setup inputs
        // so the Workflow Checker can prompt for concrete values.
        // Eager validation would reject them before the user can edit.
        return maybeInterceptDestructiveConfirm({
            type: 'plan',
            plan: templateResult,
            tier: 'workflow_template_shortcut',
            parseErrors: [],
        }, userMessage, workspaceState);
    }

    // Step 0aa: Campaign-verb shortcut. Phrases like "lead design
    // campaign", "optimize this compound", "hit-to-lead", "run a
    // campaign" describe multi-step iterative goals, not single
    // actions. The campaign shortcut catches them ahead of the
    // single-action shortcut family so they cannot be mis-aliased
    // into low-level actions (e.g., the 2026-04-23 assemble-compound
    // incident). See labwork/studies/gibbs-campaign-verbs/.
    const autonomousCampaignEnabled = opts.autonomousCampaignEnabled === true;
    const campaignIntent = autonomousCampaignEnabled
        ? matchCampaignIntent(userMessage)
        : null;
    const campaignWorkspaceSnapshot = campaignIntent?.responseClass === 'workflow'
        && workspaceState && typeof workspaceState === 'object'
        ? (Number.isInteger(workspaceState?.fragInfoCount)
            ? workspaceState
            : serializeWorkspace(workspaceState))
        : undefined;
    const campaignResult = tryCampaignShortcut(userMessage, workspaceState, {
        autonomousCampaignEnabled,
        workspaceSnapshot: campaignWorkspaceSnapshot,
    });
    if (campaignResult) {
        if (campaignResult.intent === 'workflow') {
            const cvValidation = validatePlan(
                campaignResult,
                workspaceState,
                campaignResult.executionMode === 'campaign'
                    ? {
                        // Workflow Checker binds the reviewed directed vector.
                        // The live bridge derives findType from the reviewed
                        // grow methodology before dispatch.
                        deferredRequiredInputs: {
                            'grow-from-atom': ['bondVectorPair', 'findType'],
                        },
                    }
                    : undefined,
            );
            if (!cvValidation.valid) {
                return {
                    type: 'invalid',
                    plan: campaignResult,
                    errors: cvValidation.errors,
                    tier: 'campaign_verb_shortcut',
                    parseErrors: [],
                };
            }
            return maybeInterceptDestructiveConfirm({
                type: 'plan',
                plan: campaignResult,
                tier: 'campaign_verb_shortcut',
                parseErrors: [],
            }, userMessage, workspaceState);
        }
        if (campaignResult.intent === 'clarify') {
            return {
                type: 'clarification',
                clarification: campaignResult,
                tier: 'campaign_verb_shortcut',
                parseErrors: [],
            };
        }
        if (campaignResult.intent === 'chat') {
            return {
                type: 'chat',
                message: campaignResult.message || '',
                tier: 'campaign_verb_shortcut',
                chatSource: 'shortcut',
                parseErrors: [],
            };
        }
    }

    // Step 0b: Dock-and-score shortcut. Phrases like "dock and score",
    // "score these compounds", "evaluate binding", or "rescore this"
    // deterministically build the canonical dock → minimize → score
    // pipeline against whatever protein + compounds are currently
    // loaded. Zero LLM tokens, zero hallucination risk.
    //
    // Note: this is NOT hit discovery. Hit discovery produces new
    // compounds from a library / fragment map / generative model.
    // Real hit discovery lives in a separate future shortcut — see
    // hitDiscoveryShortcut.js for the roadmap.
    const dockAndScoreResult = tryDockAndScoreShortcut(userMessage, workspaceState);
    if (dockAndScoreResult) {
        if (dockAndScoreResult.intent === 'workflow') {
            const validation = validatePlan(dockAndScoreResult, workspaceState);
            if (!validation.valid) {
                return {
                    type: 'invalid',
                    plan: dockAndScoreResult,
                    errors: validation.errors,
                    tier: 'dock_and_score_shortcut',
                    parseErrors: [],
                };
            }
            return maybeInterceptDestructiveConfirm({
                type: 'plan',
                plan: dockAndScoreResult,
                tier: 'dock_and_score_shortcut',
                parseErrors: [],
            }, userMessage, workspaceState);
        }
        if (dockAndScoreResult.intent === 'chat') {
            return {
                type: 'chat',
                message: dockAndScoreResult.message || '',
                tier: 'dock_and_score_shortcut',
                chatSource: 'shortcut',
                parseErrors: [],
            };
        }
    }

    // Step 0c: Hit discovery shortcut. "discover hits", "find hits",
    // "screen fragments". Fragment-based hit discovery pipeline:
    // grow-from-all-atoms → energy-minimize → predict-gnina-scores.
    // Produces NEW compounds, unlike dock-and-score which evaluates
    // existing ones. Requires a fragment map.
    const hitDiscoveryResult = tryHitDiscoveryShortcut(
        userMessage, workspaceState
    );
    if (hitDiscoveryResult) {
        if (hitDiscoveryResult.intent === 'workflow') {
            const hdValidation = validatePlan(
                hitDiscoveryResult, workspaceState
            );
            if (!hdValidation.valid) {
                return {
                    type: 'invalid',
                    plan: hitDiscoveryResult,
                    errors: hdValidation.errors,
                    tier: 'hit_discovery_shortcut',
                    parseErrors: [],
                };
            }
            return maybeInterceptDestructiveConfirm({
                type: 'plan',
                plan: hitDiscoveryResult,
                tier: 'hit_discovery_shortcut',
                parseErrors: [],
            }, userMessage, workspaceState);
        }
        if (hitDiscoveryResult.intent === 'chat') {
            return {
                type: 'chat',
                message: hitDiscoveryResult.message || '',
                tier: 'hit_discovery_shortcut',
                chatSource: 'shortcut',
                parseErrors: [],
            };
        }
    }

    // Step 0d: Virtual screening shortcut. "screen my library",
    // "virtual screen", "library screening". Submits compounds to
    // the Temporal-based library screening pipeline (GNINA + Boltz
    // affinity + BFD energy). Job-based, runs on GPU workers.
    const vsResult = tryVirtualScreeningShortcut(
        userMessage, workspaceState
    );
    if (vsResult) {
        if (vsResult.intent === 'workflow') {
            const vsValidation = validatePlan(vsResult, workspaceState);
            if (!vsValidation.valid) {
                return {
                    type: 'invalid',
                    plan: vsResult,
                    errors: vsValidation.errors,
                    tier: 'virtual_screening_shortcut',
                    parseErrors: [],
                };
            }
            return maybeInterceptDestructiveConfirm({
                type: 'plan',
                plan: vsResult,
                tier: 'virtual_screening_shortcut',
                parseErrors: [],
            }, userMessage, workspaceState);
        }
        if (vsResult.intent === 'chat') {
            return {
                type: 'chat',
                message: vsResult.message || '',
                tier: 'virtual_screening_shortcut',
                chatSource: 'shortcut',
                parseErrors: [],
            };
        }
    }

    // Step 0e: De novo generation shortcut. "generate new compounds",
    // "run reinvent", "de novo design". Submits seed compound to
    // REINVENT 4 for AI-driven molecular generation via RL.
    // Job-based, runs on GPU workers.
    const deNovoResult = tryDeNovoShortcut(
        userMessage, workspaceState
    );
    if (deNovoResult) {
        if (deNovoResult.intent === 'workflow') {
            const dnValidation = validatePlan(deNovoResult, workspaceState);
            if (!dnValidation.valid) {
                return {
                    type: 'invalid',
                    plan: deNovoResult,
                    errors: dnValidation.errors,
                    tier: 'de_novo_shortcut',
                    parseErrors: [],
                };
            }
            return maybeInterceptDestructiveConfirm({
                type: 'plan',
                plan: deNovoResult,
                tier: 'de_novo_shortcut',
                parseErrors: [],
            }, userMessage, workspaceState);
        }
        if (deNovoResult.intent === 'chat') {
            return {
                type: 'chat',
                message: deNovoResult.message || '',
                tier: 'de_novo_shortcut',
                chatSource: 'shortcut',
                parseErrors: [],
            };
        }
    }

    // Step 0f: Anchor resolver. Gates the LLM workflow tier — when the
    // user's request lacks a verifiable target anchor (no PDB ID, no
    // explicit protein name, or a workspace protein not relevant to the
    // stated domain), emit a clarify intent rather than letting the LLM
    // hallucinate or mimic a few-shot exemplar. Only fires for
    // workflow-tier requests; command-tier requests pass through.
    //
    // See labwork/studies/gibbs-clarify-on-ambiguous-target/ for the
    // full design. This step is the structural fix behind issue #921
    // and the 2026-04-28 breast-cancer regression.
    let anchoredProtein = null;
    if (isWorkflowRequest(userMessage)) {
        const recentHistory = getRecentEntries(5);
        const resolverStart = Date.now();
        const anchorResult = resolveAnchor(userMessage, recentHistory, workspaceState);
        const resolverDuration = Date.now() - resolverStart;

        // Telemetry — every resolver branch logs a structured entry so
        // we can audit how often each branch fires in production. Reuses
        // the existing logLLMCall sink (no LLM was actually called; we
        // store under tier='anchor_resolver' and resultType=<branch>).
        logLLMCall({
            userMessage,
            tier: 'anchor_resolver',
            systemPromptLength: 0,
            messagesCount: 0,
            model: 'none',
            responseContent: anchorResult.evidence
                || JSON.stringify({
                    status: anchorResult.status,
                    domainTerms: anchorResult.domainTerms,
                    source: anchorResult.source,
                }),
            resultType: anchorResult.status,
            retryAttempt: 0,
            parseErrors: [],
            validationErrors: [],
            duration_ms: resolverDuration,
        }).catch(() => {});

        if (anchorResult.status === 'anchored') {
            // Pass the verified protein down into the LLM prompt via
            // cached.anchoredProtein (see buildPrompt.js).
            anchoredProtein = anchorResult.protein;
        } else if (anchorResult.status === 'ambiguous') {
            const clarification = (anchorResult.domainTerms && anchorResult.domainTerms.length > 0)
                ? buildTargetSelectionClarification(anchorResult.domainTerms[0])
                : buildGenericTargetClarification();
            return {
                type: 'clarification',
                clarification,
                tier: 'anchor_resolver',
                parseErrors: [],
            };
        } else if (anchorResult.status === 'needsConfirmation') {
            return {
                type: 'clarification',
                clarification: buildTargetConfirmationClarification({
                    candidate: anchorResult.candidate,
                    domainSlug: anchorResult.domainSlug,
                    evidence: anchorResult.evidence,
                }),
                tier: 'anchor_resolver',
                parseErrors: [],
            };
        }
    }

    // Steps 1-4: Route → classify → plan → validate, with a retry
    // loop on validation failure. This is the validation-retry sensor
    // for the anti-hallucination pathway (Gap 1 from the harness
    // engineering analysis). When validatePlan rejects a plan, we
    // re-prompt the LLM with the validator's error messages and the
    // rejected plan so it can self-correct. Max 2 retries before
    // giving up and surfacing the error to the user as before.
    //
    // Most LLM hallucinations on structured output (invented action
    // IDs, wrong bucket for literal inputs, malformed inputsFrom
    // references) are correctable when the model sees its own mistake
    // in-session. Without this loop, every validation failure became
    // a dead end that the user had to resolve by rephrasing the prompt.
    const MAX_RETRIES = 2;
    let classified = null;
    let tier = null;
    let parseErrors = [];
    let plan = null;
    let validation = null;
    let retryContext;

    // Track whether the loop exited with a validated result. Separate
    // from `validation.valid` because the loop now handles both plan
    // and clarify validation paths; a successful exit can come from
    // either path.
    let loopExitResult = null;

    for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
        // Step 1-2: Route and get classified intent (with retry context on attempts > 0)
        // anchoredProtein, when set by step 0f above, is passed through so
        // buildPrompt can surface it in the system prompt.
        /* eslint-disable no-await-in-loop */
        const result = await routeRequest(
            userMessage, workspaceState, retryContext, { anchoredProtein }
        );
        /* eslint-enable no-await-in-loop */
        classified = result.classified;
        tier = result.tier;
        parseErrors = result.parseErrors;

        // Chat intent is always a valid terminal state — the LLM is
        // answering a non-action question. Return immediately.
        if (classified.intent === 'chat') {
            loopExitResult = {
                type: 'chat',
                message: classified.message || '',
                tier,
                chatSource: 'llm',
                parseErrors,
                retryAttempts: attempt,
            };
            break;
        }

        // Clarify intent: validate that context.actionId is set and
        // points to a real action, otherwise the user will hit the
        // "could not find the action to resume" error when they answer.
        // Malformed clarifies feed back into the retry loop the same
        // way invalid plans do.
        if (classified.intent === 'clarify') {
            const clarifyValidation = validateClarifyIntent(classified);
            if (clarifyValidation.valid) {
                if (attempt > 0) {
                    // eslint-disable-next-line no-console
                    console.info(
                        `[Gibbs] Clarify intent recovered after ${attempt}`
                        + ` retry attempt${attempt === 1 ? '' : 's'}`
                    );
                }
                loopExitResult = {
                    type: 'clarification',
                    clarification: classified,
                    tier,
                    parseErrors,
                    retryAttempts: attempt,
                };
                break;
            }

            // Invalid clarify — prepare retry context. The retry prompt
            // will tell the LLM exactly what was missing / wrong with
            // its clarify response.
            if (attempt < MAX_RETRIES) {
                // eslint-disable-next-line no-console
                console.warn(
                    `[Gibbs] Clarify intent validation failed (attempt ${attempt + 1}/${MAX_RETRIES + 1}),`
                    + ' re-prompting LLM with errors:',
                    clarifyValidation.errors
                );
                retryContext = {
                    previousResponse: classified,
                    errors: clarifyValidation.errors,
                    attempt: attempt + 1,
                };
                continue;
            }

            // All retries exhausted on a malformed clarify. Fall back
            // to a helpful chat message rather than surfacing a broken
            // clarification to the user.
            // eslint-disable-next-line no-console
            console.warn(
                '[Gibbs] Clarify intent validation failed after all retries;'
                + ' falling back to chat.',
                clarifyValidation.errors
            );
            loopExitResult = {
                type: 'chat',
                message: 'I\'d like to ask a clarifying question but'
                    + ' I\'m having trouble formulating one. Could you'
                    + ' rephrase your request with more specifics about'
                    + ' which action or parameters you want?',
                tier,
                chatSource: 'llm',
                parseErrors,
                retryAttempts: MAX_RETRIES + 1,
            };
            break;
        }

        // Step 3: Compose action plan (workflow / single_action intents)
        plan = planActions(classified, workspaceState);

        // Step 4: Validate
        validation = validatePlan(plan, workspaceState);

        if (validation.valid) {
            // Success path — break out of the retry loop
            if (attempt > 0) {
                // eslint-disable-next-line no-console
                console.info(
                    `[Gibbs] Plan validation recovered after ${attempt}`
                    + ` retry attempt${attempt === 1 ? '' : 's'}`
                );
            }
            break;
        }

        // Invalid plan — prepare retry context for the next iteration
        if (attempt < MAX_RETRIES) {
            // eslint-disable-next-line no-console
            console.warn(
                `[Gibbs] Plan validation failed (attempt ${attempt + 1}/${MAX_RETRIES + 1}),`
                + ' re-prompting LLM with errors:',
                validation.errors
            );
            retryContext = {
                previousResponse: classified,
                errors: validation.errors,
                attempt: attempt + 1,
            };
        }
    }

    // If the loop broke with a non-plan result (chat or clarification),
    // return it directly.
    if (loopExitResult) {
        return loopExitResult;
    }

    // If we fell out of the loop with an invalid plan, all retries
    // were exhausted. Surface the final error to the user.
    if (!validation || !validation.valid) {
        return {
            type: 'invalid',
            plan,
            errors: (validation && validation.errors) || ['Unknown validation failure'],
            tier,
            parseErrors,
            retryAttempts: MAX_RETRIES + 1,
        };
    }

    return maybeInterceptDestructiveConfirm({
        type: 'plan',
        plan,
        tier,
        parseErrors,
    }, userMessage, workspaceState);
}

/**
 * Convert a validated ActionPlan into a Workflow DAG.
 * Convenience function that bridges text-to-action output to the workflow engine.
 *
 * @param {Object} actionPlan - ActionPlan from textToAction() result.plan
 * @returns {{ workflow: import('../workflow/Workflow.js').Workflow, warnings: string[] }}
 */
export function actionPlanToWorkflow(actionPlan) {
    return createFromActionPlan(actionPlan);
}

/**
 * Resume from a clarification response.
 *
 * @param {Object} clarificationContext - Previous clarification context
 * @param {*} userAnswer - User's selected option or typed answer
 * @param {Object} workspaceState - Current workspace state
 * @returns {{ plan: Object|null, needsMoreClarification: Object|null }}
 */
export { resumeFromClarification };

// Re-export utilities for testing and advanced usage
export { routeRequest } from './routeRequest';
export { buildCommandPrompt, buildWorkflowPrompt } from './buildPrompt';
export { classifyIntent } from './classifyIntent';
export { planActions } from './planActions';
export { validatePlan } from './validatePlan';
export { extractParams } from './extractParams';
export { validateParams } from './validateParams';
export { generateClarification, generateAmbiguousClarification } from './clarification';
export { serializeAction, serializeRegistryContext, estimateTokens } from './serializeRegistry';
export { serializeWorkspace } from './serializeWorkspace';
export { filterForCommandTier, filterForWorkflowTier } from './filterActions';
export { sendLLMRequest, setEndpoint } from './llmClient';
export { retrieveDocs, setRetrieveEndpoint } from './docRetriever';
export { parseResponse } from './parseResponse';
export { isWorkflowRequest } from './routeRequest';
export { resolveRefs } from './resolveRefs';

/**
 * @typedef {Object} TextToActionResult
 * @property {'plan' | 'clarification' | 'chat' | 'invalid'} type
 * @property {Object} [plan] - Validated action plan (when type is 'plan')
 * @property {Object} [clarification] - Clarification request (when type is 'clarification')
 * @property {string} [message] - Chat response (when type is 'chat')
 * @property {string[]} [errors] - Validation errors (when type is 'invalid')
 * @property {string} tier - Which LLM tier was used ('command' | 'workflow')
 * @property {string[]} parseErrors - Any parsing warnings
 */
